/**
 * Language support configuration for StoryBaib
 * 
 * This file defines supported languages for multi-language storybook generation.
 * Languages are selected based on popularity for children's content and 
 * OpenAI's strong support for accurate generation.
 */

import { detectScript } from './script-detection';

export interface Language {
  code: string;           // ISO 639-1 language code
  name: string;           // English name for UI
  nativeName: string;     // Native language name
  flag: string;           // Unicode flag emoji
  rtl?: boolean;          // Right-to-left text direction
  aiPromptName: string;   // How to refer to this language in AI prompts
  sampleTitle: string;    // Example title in this language
  titlePlaceholder: string; // Placeholder text for title field
  detailsPlaceholder: string; // Placeholder text for details field
}

export const SUPPORTED_LANGUAGES: Language[] = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    aiPromptName: 'English',
    sampleTitle: "Emma's Magical Adventure",
    titlePlaceholder: "e.g., Emma's Magical Adventure",
    detailsPlaceholder: "Imagine a story with your child as the hero! Describe their magical adventure, special powers, mythical friends, or the enchanted world they'll explore."
  },
  {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    flag: '🇪🇸',
    aiPromptName: 'Spanish',
    sampleTitle: "La Aventura Mágica de Emma",
    titlePlaceholder: "ej., La Aventura Mágica de Emma",
    detailsPlaceholder: "¡Imagina una historia con tu hijo como el héroe! Describe su aventura mágica, poderes especiales, amigos míticos, o el mundo encantado que explorará."
  },
  {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    flag: '🇫🇷',
    aiPromptName: 'French',
    sampleTitle: "L'Aventure Magique d'Emma",
    titlePlaceholder: "ex., L'Aventure Magique d'Emma",
    detailsPlaceholder: "Imaginez une histoire avec votre enfant comme héros ! Décrivez leur aventure magique, pouvoirs spéciaux, amis mythiques, ou le monde enchanté qu'ils exploreront."
  },
  {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    flag: '🇩🇪',
    aiPromptName: 'German',
    sampleTitle: "Emmas Magisches Abenteuer",
    titlePlaceholder: "z.B., Emmas Magisches Abenteuer",
    detailsPlaceholder: "Stellen Sie sich eine Geschichte vor, in der Ihr Kind der Held ist! Beschreiben Sie sein magisches Abenteuer, besondere Kräfte, mythische Freunde oder die verzauberte Welt, die es erkunden wird."
  },
  {
    code: 'it',
    name: 'Italian',
    nativeName: 'Italiano',
    flag: '🇮🇹',
    aiPromptName: 'Italian',
    sampleTitle: "L'Avventura Magica di Emma",
    titlePlaceholder: "es., L'Avventura Magica di Emma",
    detailsPlaceholder: "Dicci cosa includere: gli interessi del bambino, giocattoli preferiti, attività, animali, ecc. Più dettagli rendono la storia più personalizzata!"
  },
  {
    code: 'pt',
    name: 'Portuguese',
    nativeName: 'Português',
    flag: '🇧🇷', // Brazilian Portuguese is the default variant (European on request, see LANGUAGE_SPECIFIC_GUIDANCE)
    aiPromptName: 'Portuguese',
    sampleTitle: "A Aventura Mágica da Emma",
    titlePlaceholder: "ex., A Aventura Mágica da Emma",
    detailsPlaceholder: "Conte-nos o que incluir: os interesses da criança, brinquedos favoritos, atividades, animais, etc. Mais detalhes tornam a história mais personalizada!"
  },
  {
    code: 'nl',
    name: 'Dutch',
    nativeName: 'Nederlands',
    flag: '🇳🇱',
    aiPromptName: 'Dutch',
    sampleTitle: "Emma's Magische Avontuur",
    titlePlaceholder: "bijv., Emma's Magische Avontuur",
    detailsPlaceholder: "Vertel ons wat erin moet komen: de interesses van het kind, favoriete speelgoed, activiteiten, dieren, enz. Meer details maken het verhaal persoonlijker!"
  },
  {
    code: 'sv',
    name: 'Swedish',
    nativeName: 'Svenska',
    flag: '🇸🇪',
    aiPromptName: 'Swedish',
    sampleTitle: "Emmas Magiska Äventyr",
    titlePlaceholder: "t.ex., Emmas Magiska Äventyr",
    detailsPlaceholder: "Berätta vad som ska inkluderas: barnets intressen, favoritleksaker, aktiviteter, djur, etc. Fler detaljer gör berättelsen mer personlig!"
  },
  {
    code: 'da',
    name: 'Danish',
    nativeName: 'Dansk',
    flag: '🇩🇰',
    aiPromptName: 'Danish',
    sampleTitle: "Emmas Magiske Eventyr",
    titlePlaceholder: "f.eks., Emmas Magiske Eventyr",
    detailsPlaceholder: "Fortæl os hvad der skal inkluderes: barnets interesser, favoritlegetøj, aktiviteter, dyr, etc. Flere detaljer gør historien mere personlig!"
  },
  {
    code: 'no',
    name: 'Norwegian',
    nativeName: 'Norsk',
    flag: '🇳🇴',
    aiPromptName: 'Norwegian (Bokmål)', // Bokmål, not Nynorsk: the written standard ~90% of Norwegians use
    sampleTitle: "Emmas Magiske Eventyr",
    titlePlaceholder: "f.eks., Emmas Magiske Eventyr",
    detailsPlaceholder: "Fortell oss hva som skal inkluderes: barnets interesser, favorittleker, aktiviteter, dyr, etc. Flere detaljer gjør historien mer personlig!"
  },
  {
    code: 'fi',
    name: 'Finnish',
    nativeName: 'Suomi',
    flag: '🇫🇮',
    aiPromptName: 'Finnish',
    sampleTitle: "Emman Taikaseikkailu",
    titlePlaceholder: "esim., Emman Taikaseikkailu",
    detailsPlaceholder: "Kerro mitä sisällyttää: lapsen kiinnostukset, lempilelut, aktiviteetit, eläimet, jne. Enemmän yksityiskohtia tekee tarinasta henkilökohtaisemman!"
  },
  {
    code: 'zh',
    name: 'Chinese (Simplified)',
    nativeName: '中文(简体)',
    flag: '🇨🇳',
    aiPromptName: 'Simplified Chinese',
    sampleTitle: "艾玛的神奇冒险",
    titlePlaceholder: "例如：艾玛的神奇冒险",
    detailsPlaceholder: "想象一个以您的孩子为英雄的故事！描述他们的神奇冒险、特殊能力、神话朋友或他们将探索的魔法世界。"
  },
  {
    code: 'ja',
    name: 'Japanese',
    nativeName: '日本語',
    flag: '🇯🇵',
    aiPromptName: 'Japanese',
    sampleTitle: "エマの魔法の冒険",
    titlePlaceholder: "例：エマの魔法の冒険",
    detailsPlaceholder: "含めたい内容を教えてください：子供の興味、好きなおもちゃ、活動、動物など。詳細が多いほど、より個人的な物語になります！"
  },
  {
    code: 'ko',
    name: 'Korean',
    nativeName: '한국어',
    flag: '🇰🇷',
    aiPromptName: 'Korean',
    sampleTitle: "에마의 마법 모험",
    titlePlaceholder: "예: 에마의 마법 모험",
    detailsPlaceholder: "포함할 내용을 알려주세요: 아이의 관심사, 좋아하는 장난감, 활동, 동물 등. 더 많은 세부사항이 이야기를 더 개인적으로 만들어줍니다!"
  },
  {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    flag: '🇸🇦',
    rtl: true,
    aiPromptName: 'Arabic',
    sampleTitle: "مغامرة إيما السحرية",
    titlePlaceholder: "مثال: مغامرة إيما السحرية",
    detailsPlaceholder: "أخبرنا بما تريد تضمينه: اهتمامات الطفل، الألعاب المفضلة، الأنشطة، الحيوانات، إلخ. كلما زادت التفاصيل، كانت القصة أكثر تخصيصاً!"
  },
  {
    code: 'he',
    name: 'Hebrew',
    nativeName: 'עברית',
    flag: '🇮🇱',
    rtl: true,
    aiPromptName: 'Hebrew',
    sampleTitle: "ההרפתקה הקסומה של אמה",
    titlePlaceholder: "למשל: ההרפתקה הקסומה של אמה",
    detailsPlaceholder: "ספרו לנו מה לכלול: תחומי עניין של הילד, צעצועים מועדפים, פעילויות, חיות וכו'. יותר פרטים הופכים את הסיפור לאישי יותר!"
  },
  {
    code: 'ru',
    name: 'Russian',
    nativeName: 'Русский',
    flag: '🇷🇺',
    aiPromptName: 'Russian',
    sampleTitle: "Волшебное приключение Эммы",
    titlePlaceholder: "например, Волшебное приключение Эммы",
    detailsPlaceholder: "Расскажите, что включить: интересы ребенка, любимые игрушки, занятия, животные и т.д. Чем больше деталей, тем более персонализированной будет история!"
  },
  {
    code: 'hi',
    name: 'Hindi',
    nativeName: 'हिंदी',
    flag: '🇮🇳',
    aiPromptName: 'Hindi',
    sampleTitle: "एम्मा का जादुई साहसिक",
    titlePlaceholder: "जैसे: एम्मा का जादुई साहसिक",
    detailsPlaceholder: "बताएं कि क्या शामिल करना है: बच्चे की रुचियां, पसंदीदा खिलौने, गतिविधियां, जानवर आदि। अधिक विवरण कहानी को अधिक व्यक्तिगत बनाते हैं!"
  },
  {
    code: 'pa',
    name: 'Punjabi',
    nativeName: 'ਪੰਜਾਬੀ',
    flag: '🇮🇳',
    aiPromptName: 'Punjabi',
    sampleTitle: "ਐਮਾ ਦਾ ਜਾਦੂਗਰ ਸਾਹਸ",
    titlePlaceholder: "ਜਿਵੇਂ: ਐਮਾ ਦਾ ਜਾਦੂਗਰ ਸਾਹਸ",
    detailsPlaceholder: "ਦੱਸੋ ਕਿ ਕਹਾਣੀ ਵਿੱਚ ਕੀ ਸ਼ਾਮਲ ਕਰਨਾ ਹੈ: ਬੱਚੇ ਦੀਆਂ ਰੁਚੀਆਂ, ਮਨਪਸੰਦ ਖਿਡੌਣੇ, ਗਤੀਵਿਧੀਆਂ, ਜਾਨਵਰ ਆਦਿ। ਜ਼ਿਆਦਾ ਵੇਰਵੇ ਕਹਾਣੀ ਨੂੰ ਹੋਰ ਵਿਅਕਤੀਗਤ ਬਣਾਉਂਦੇ ਹਨ!"
  },
  {
    code: 'mr',
    name: 'Marathi',
    nativeName: 'मराठी',
    flag: '🇮🇳',
    aiPromptName: 'Marathi',
    sampleTitle: "एम्माचे जादुई साहस",
    titlePlaceholder: "उदा: एम्माचे जादुई साहस",
    detailsPlaceholder: "कथेत काय समाविष्ट करायचे ते सांगा: मुलाची आवड, आवडते खेळणी, क्रियाकलाप, प्राणी इ. जास्त तपशील कथा अधिक वैयक्तिक बनवतात!"
  },
  {
    code: 'te',
    name: 'Telugu',
    nativeName: 'తెలుగు',
    flag: '🇮🇳',
    aiPromptName: 'Telugu',
    sampleTitle: "ఎమ్మా యొక్క మాయా సాహసం",
    titlePlaceholder: "ఉదా: ఎమ్మా యొక్క మాయా సాహసం",
    detailsPlaceholder: "కథలో ఏమి చేర్చాలో చెప్పండి: పిల్లల ఆసక్తులు, ఇష్టమైన బొమ్మలు, కార్యకలాపాలు, జంతువులు మొదలైనవి. ఎక్కువ వివరాలు కథను మరింత వ్యక్తిగతం చేస్తాయి!"
  },
  {
    code: 'ta',
    name: 'Tamil',
    nativeName: 'தமிழ்',
    flag: '🇮🇳',
    aiPromptName: 'Tamil',
    sampleTitle: "எம்மாவின் மாயாஜால சாகசம்",
    titlePlaceholder: "எ.கா: எம்மாவின் மாயாஜால சாகசம்",
    detailsPlaceholder: "கதையில் என்ன சேர்க்க வேண்டும் என்று சொல்லுங்கள்: குழந்தையின் விருப்பங்கள், பிடித்த பொம்மைகள், செயல்பாடுகள், விலங்குகள் போன்றவை. அதிக விவரங்கள் கதையை மிகவும் தனிப்பட்டதாக ஆக்குகின்றன!"
  },
  {
    code: 'ml',
    name: 'Malayalam',
    nativeName: 'മലയാളം',
    flag: '🇮🇳',
    aiPromptName: 'Malayalam',
    sampleTitle: "എമ്മയുടെ മാന്ത്രിക സാഹസം",
    titlePlaceholder: "ഉദാ: എമ്മയുടെ മാന്ത്രിക സാഹസം",
    detailsPlaceholder: "കഥയിൽ എന്ത് ഉൾപ്പെടുത്തണമെന്ന് പറയുക: കുട്ടിയുടെ താൽപ്പര്യങ്ങൾ, പ്രിയപ്പെട്ട കളിപ്പാട്ടങ്ങൾ, പ്രവർത്തനങ്ങൾ, മൃഗങ്ങൾ മുതലായവ. കൂടുതൽ വിശദാംശങ്ങൾ കഥയെ കൂടുതൽ വ്യക്തിഗതമാക്കുന്നു!"
  },
  {
    code: 'kn',
    name: 'Kannada',
    nativeName: 'ಕನ್ನಡ',
    flag: '🇮🇳',
    aiPromptName: 'Kannada',
    sampleTitle: "ಎಮ್ಮಾಳ ಮಾಂತ್ರಿಕ ಸಾಹಸ",
    titlePlaceholder: "ಉದಾ: ಎಮ್ಮಾಳ ಮಾಂತ್ರಿಕ ಸಾಹಸ",
    detailsPlaceholder: "ಕಥೆಯಲ್ಲಿ ಏನನ್ನು ಸೇರಿಸಬೇಕೆಂದು ಹೇಳಿ: ಮಗುವಿನ ಆಸಕ್ತಿಗಳು, ಮೆಚ್ಚಿನ ಆಟಿಕೆಗಳು, ಚಟುವಟಿಕೆಗಳು, ಪ್ರಾಣಿಗಳು ಇತ್ಯಾದಿ. ಹೆಚ್ಚಿನ ವಿವರಗಳು ಕಥೆಯನ್ನು ಹೆಚ್ಚು ವೈಯಕ್ತಿಕಗೊಳಿಸುತ್ತವೆ!"
  }
];

/**
 * Languages the site offers for new books (picture books and comics): the Latin-alphabet ones only, since
 * the art models cannot letter other scripts reliably. SUPPORTED_LANGUAGES keeps all 24 so books already
 * made in another language still open, print and resume.
 */
export const OFFERED_LANGUAGE_CODES = ['en', 'es', 'fr', 'de', 'it', 'pt', 'nl', 'sv', 'da', 'no', 'fi'] as const;

export function isOfferedLanguage(code: string): boolean {
  return (OFFERED_LANGUAGE_CODES as readonly string[]).includes(code);
}

/** The offered languages, in SUPPORTED_LANGUAGES order. */
export function offeredLanguages(): Language[] {
  return SUPPORTED_LANGUAGES.filter((l) => isOfferedLanguage(l.code));
}

/** Language options for the story-language select. `keep` re-adds a resumed book's retired language. */
export function getOfferedLanguageOptions(keep?: string): { value: string; label: string; searchLabel: string }[] {
  return SUPPORTED_LANGUAGES
    .filter((l) => isOfferedLanguage(l.code) || l.code === keep)
    .map((lang) => ({
      value: lang.code,
      label: `${lang.name} (${lang.nativeName})`,
      searchLabel: `${lang.name} ${lang.nativeName}`,
    }));
}

/** Comics are offered in the same languages as picture books. */
export const COMIC_LANGUAGE_CODES = OFFERED_LANGUAGE_CODES;

export function isComicLanguage(code: string): boolean {
  return (COMIC_LANGUAGE_CODES as readonly string[]).includes(code);
}

/**
 * Get language by code
 */
export function getLanguageByCode(code: string): Language | undefined {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code);
}

/**
 * Get default language (English)
 */
export function getDefaultLanguage(): Language {
  return SUPPORTED_LANGUAGES[0]; // English is first
}

/**
 * Check if a language code is supported
 */
export function isLanguageSupported(code: string): boolean {
  return SUPPORTED_LANGUAGES.some(lang => lang.code === code);
}

/**
 * Get language options for form select
 */
export function getLanguageOptions() {
  return SUPPORTED_LANGUAGES.map(lang => ({
    value: lang.code,
    label: `${lang.name} (${lang.nativeName})`, // Changed to avoid flag emoji issues
    searchLabel: `${lang.name} ${lang.nativeName}`
  }));
}

/**
 * Rules that only one language needs, kept out of every other language's prompt. Shared by story
 * generation (getLanguagePromptInstruction) and the title/details translation routes so every path
 * that writes a name or picks a variant agrees.
 */
const LANGUAGE_SPECIFIC_GUIDANCE: Record<string, string> = {
  // Japanese: the generic "transliterate phonetically" rule turned Japanese names typed in Latin
  // letters ("Aoi", "Haruto") into katakana, which reads as a foreign name. We cannot know the
  // family's kanji, so Japanese names go in hiragana; foreign names go in katakana.
  ja: `CHARACTER NAMES IN JAPANESE (REQUIRED):
- Never write a character name in Latin/English letters.
- A name typed in Japanese script (hiragana, katakana or kanji) is kept exactly as typed.
- A Japanese name typed in Latin letters is written in hiragana: "Aoi" → "あおい", "Haruto" → "はると", "Yui" → "ゆい", "Mio" → "みお", "Sota" → "そうた", "Hina" → "ひな".
- Never invent kanji for a name: we do not know which kanji the family uses, so hiragana is the safe, natural choice.
- A non-Japanese name typed in Latin letters is written in katakana: "Emma" → "エマ", "Arjun" → "アルジュン", "Lucas" → "ルーカス", "Olivia" → "オリビア".
- Write each name the same way every time it appears, including in the title.`,
  // Portuguese: the prompt used to say only "Portuguese". Brazilian is the default (largest audience;
  // samples and landing copy are Brazilian); European when the family asks for it.
  pt: `PORTUGUESE VARIANT (REQUIRED):
- Write in Brazilian Portuguese (português do Brasil) by default.
- Write in European Portuguese (português europeu) only when the title, story idea or details clearly ask for it: the family lives in Portugal or in a Portuguese city (Lisboa/Lisbon, Porto (the city on its own), Coimbra, Braga, Faro...), on the ilha da Madeira or in the Açores/Azores, or they write "português europeu", "português de Portugal", "European Portuguese", "Portuguese from Portugal" or "PT-PT".
- NOT a request for European Portuguese (stay Brazilian): Brazilian places, even when they contain a Portuguese name (Porto Alegre, Porto Seguro, Porto Velho, Rio Madeira); the everyday word "madeira" (wood, e.g. "casa de madeira"); Portuguese food, animals or breeds (e.g. Portuguese water dog); a Brazilian family visiting or travelling to Portugal or Lisbon. When in doubt, use Brazilian Portuguese.
- Brazilian Portuguese: "você" for "you"; the gerund for ongoing actions ("está brincando", "estão correndo"); Brazilian words (ônibus, trem, café da manhã, sorvete, geladeira, celular, banheiro, menino/menina, mamãe/papai); Brazilian spelling (econômico, fato, ação).
- European Portuguese: "tu" for "you" within the family and between friends; "a + infinitive" for ongoing actions ("está a brincar", "estão a correr"); European words (autocarro, comboio, pequeno-almoço, gelado, frigorífico, telemóvel, casa de banho, miúdo/miúda, mamã/papá); European spelling (económico, facto, ação).
- Never mix the two variants: the title, every page and every line of dialogue use the same variant.`,
};

/**
 * Language-specific name/variant guidance ('' when the language has none).
 */
export function getLanguageSpecificGuidance(languageCode: string): string {
  return LANGUAGE_SPECIFIC_GUIDANCE[languageCode] || '';
}

/**
 * Guidance for the title/details translation routes: the language-specific rule plus, when given,
 * the story details as context, so a title translated on its own picks the same variant (and name
 * spellings) as the story. '' for languages without a specific rule.
 */
export function getTranslationGuidance(languageCode: string, context?: string): string {
  const guidance = getLanguageSpecificGuidance(languageCode);
  if (!guidance) return '';
  const details = (context || '').trim().slice(0, 2000);
  if (!details) return guidance;
  return `${guidance}

Story details (use them only to choose the language variant and the spelling of names; do not translate them or include them in your answer):
"""
${details}
"""
Apply the rule above to these story details first: your answer must use the same variant and the same name spellings that the story will use.`;
}

/**
 * Format AI prompt instruction for language
 */
export function getLanguagePromptInstruction(languageCode: string): string {
  const language = getLanguageByCode(languageCode);
  if (!language) {
    // Default to English if language not found
    return 'CRITICAL: Generate the entire story in English. All story text must be written in English. Use natural, age-appropriate English language that children would understand and enjoy.';
  }

  // Non-Latin script languages that require name transliteration
  const nonLatinScriptLanguages = [
    'zh', 'ja', 'ko',  // CJK
    'ar', 'he',        // RTL scripts
    'hi', 'mr', 'bn', 'ta', 'te', 'kn', 'ml', 'gu', 'pa', 'ne',  // Indic scripts
    'ru', 'uk',        // Cyrillic
    'th', 'am', 'ka'   // Other scripts
  ];

  const requiresNameTransliteration = nonLatinScriptLanguages.includes(languageCode);

  // Always provide explicit language instruction, even for English
  let instruction = `CRITICAL: Generate the entire story in ${language.aiPromptName}. All story text must be written in ${language.aiPromptName}${language.code === 'en' ? '' : ', not English'}. Use natural, age-appropriate ${language.aiPromptName} language that children would understand and enjoy.`;

  const specificGuidance = getLanguageSpecificGuidance(languageCode);

  // Japanese has its own name rule (hiragana vs katakana) instead of the generic transliteration one.
  if (languageCode === 'ja') {
    instruction += `

${specificGuidance}`;
  } else if (requiresNameTransliteration) {
    // Add name transliteration instruction for non-Latin scripts
    instruction += `

CHARACTER NAME TRANSLITERATION (REQUIRED):
- ALL character names MUST be written in ${language.aiPromptName} script, NOT in Latin/English letters
- Transliterate names phonetically to the ${language.aiPromptName} writing system
- For example: "Arjun" should be "अर्जुन" in Hindi, "アルジュン" in Japanese, "阿尔琼" in Chinese
- The reader should see names in their native script throughout the story
- Keep the pronunciation similar to the original name when transliterating`;

    // Models slip words from sibling scripts (e.g. Devanagari inside Kannada); forbid it explicitly.
    // zh/ja/ko legitimately mix scripts, so they are excluded.
    if (!["zh", "ja", "ko"].includes(languageCode)) {
      instruction += `

Use only ${detectScript(languageCode)} script for every word, including character names; never use words from any other language or script.`;
    }
  }

  // Other language-specific rules (e.g. the Portuguese variant) go after the generic ones.
  if (languageCode !== 'ja' && specificGuidance) {
    instruction += `

${specificGuidance}`;
  }

  return instruction;
}

// ============= EMBEDDED TITLE SUPPORT FUNCTIONS =============

/**
 * Languages that support embedded title generation via GPT-Image-1
 * These languages render well when titles are generated directly in the image
 */
export const LANGUAGES_WITH_EMBEDDED_TITLES = [
  // Latin script languages (11)
  'en', // English
  'es', // Spanish
  'fr', // French
  'de', // German
  'it', // Italian
  'pt', // Portuguese
  'nl', // Dutch
  'sv', // Swedish
  'no', // Norwegian
  'da', // Danish
  'fi', // Finnish
  
  // Cyrillic script languages (2)
  'ru', // Russian
  'uk', // Ukrainian
  
  // Asian languages (3)
  'zh', // Chinese (Simplified/Traditional)
  'ja', // Japanese
  'ko', // Korean
] as const;

export type EmbeddedTitleLanguage = typeof LANGUAGES_WITH_EMBEDDED_TITLES[number];

/**
 * Typography hints for different language scripts
 */
const TYPOGRAPHY_HINTS: Record<string, string> = {
  'zh': 'Use professional Chinese characters suitable for book covers',
  'ja': 'Use clear hiragana, katakana, or kanji with professional book styling',
  'ko': 'Use clear Hangul characters with professional book cover styling',
  'ar': 'Use decorative Arabic script reading right-to-left with professional styling',
  'he': 'Use Hebrew letters reading right-to-left with professional book styling',
  'ru': 'Use Cyrillic letters with professional book cover styling',
  'uk': 'Use Ukrainian Cyrillic with professional book cover styling',
  'default': 'Professional book cover typography'
};

/**
 * Check if a language supports embedded title generation
 * UPDATED: Latin-script languages now support embedded titles again
 * Non-Latin scripts continue to use overlay for proper rendering
 */
export function shouldUseEmbeddedTitle(languageCode: string): boolean {
  // Feature flag for safe rollout
  if (process.env.ENABLE_EMBEDDED_TITLES === 'false') {
    return false; // Emergency killswitch
  }
  
  // Latin-script languages that render well with embedded titles
  const LATIN_SCRIPT_LANGUAGES = [
    'en', // English
    'es', // Spanish
    'fr', // French
    'de', // German
    'it', // Italian
    'pt', // Portuguese
    'nl', // Dutch
    'sv', // Swedish
    'no', // Norwegian
    'da', // Danish
    'fi', // Finnish
  ];
  
  // Check if this is a Latin-script language
  const supportsEmbedded = LATIN_SCRIPT_LANGUAGES.includes(languageCode);
  
  if (supportsEmbedded) {
    console.log(`[Embedded Title] Language ${languageCode} will use embedded title`);
  }
  
  return supportsEmbedded;
}

/**
 * Get the display name for a language
 */
export function getLanguageName(languageCode: string): string {
  const language = getLanguageByCode(languageCode);
  return language?.name || 'English';
}

/**
 * Get typography hints for title generation
 */
export function getTypographyHint(languageCode: string): string {
  return TYPOGRAPHY_HINTS[languageCode] || TYPOGRAPHY_HINTS['default'];
}

/**
 * Generate title prompt enhancement for image generation
 * UPDATED: Generates embedding instructions for Latin scripts with style adaptation
 */
export function generateTitlePrompt(title: string, languageCode: string, bookStyle?: string): string {
  if (!shouldUseEmbeddedTitle(languageCode)) {
    return ''; // No embedded title for non-Latin scripts
  }
  
  // Get base typography hint for the language
  const typographyHint = getTypographyHint(languageCode);
  
  // STYLE ADAPTATION: Adapt title style to book theme
  let styleSpecificInstructions = '';
  const styleLower = (bookStyle || '').toLowerCase();
  
  if (styleLower.includes('fantasy') || styleLower.includes('magical')) {
    styleSpecificInstructions = `
- Use decorative, whimsical lettering with magical flourishes
- Consider adding sparkles, stars, or subtle magical effects around the letters
- Font style should evoke wonder and enchantment`;
  } else if (styleLower.includes('cartoon') || styleLower.includes('anime')) {
    styleSpecificInstructions = `
- Use bold, bubbly cartoon-style lettering
- Bright, vibrant colors with strong outlines
- Fun, playful font that matches animated style`;
  } else if (styleLower.includes('realistic') || styleLower.includes('photograph')) {
    styleSpecificInstructions = `
- Use clean, professional typography (like movie poster titles)
- Clear, readable sans-serif font
- Bright, bold colors with strong contrast for readability`;
  } else if (styleLower.includes('watercolor') || styleLower.includes('painted')) {
    styleSpecificInstructions = `
- Use soft, artistic lettering that looks hand-painted
- Gentle color gradients and watercolor textures in the letters
- Organic, flowing font style`;
  } else if (styleLower.includes('3d') || styleLower.includes('pixar')) {
    styleSpecificInstructions = `
- Use three-dimensional lettering with depth and shadows
- Modern, animated movie poster style typography
- Bold and impactful with subtle 3D effects`;
  } else if (styleLower.includes('comic') || styleLower.includes('graphic novel')) {
    styleSpecificInstructions = `
- Use comic book style lettering
- Bold, dynamic fonts with action-oriented styling
- Strong contrast and graphic novel aesthetics`;
  } else {
    // Default style - use professional typography to avoid AI hallucinations
    styleSpecificInstructions = `
- Use clean, professional typography (like movie poster titles)
- Clear, readable sans-serif font
- Bright, bold colors with strong contrast for readability`;
  }
  
  return `
TITLE RENDERING:
Display the title: "${title}"
- Typography: ${typographyHint}
${styleSpecificInstructions}

📏 SIZE AND POSITION REQUIREMENTS (BOOK COVER STYLE):
1. The title should be embedded at the BOTTOM-CENTER of the image, overlaid on the scene
2. This is a BOOK COVER - the title should be LARGE and PROMINENT, not a footnote
3. The title can occupy UP TO 25% of the image height depending on title length
4. Position: Bottom area of the image, HORIZONTALLY CENTERED with equal margins on left and right
5. Layout: Use 2-3 lines if needed for better readability of long titles
6. The title should be the second most prominent element after the characters
7. 🚨 HORIZONTAL CENTERING: The title text must be centered horizontally in the image - NOT aligned left or right
   - Calculate the exact horizontal center of the image: width / 2
   - Position the title so its CENTER aligns with the image center
   - WRONG: |Title text aligned to left edge               |
   - WRONG: |               Title text aligned to right edge|
   - CORRECT: |          Title centered in middle          |

⚠️ CRITICAL TEXT FITTING AND READABILITY REQUIREMENTS:
1. The ENTIRE title must fit within the available space with margins
2. MANDATORY MARGINS (measured from image edges):
   - Left margin: minimum 60 pixels from left edge
   - Right margin: minimum 60 pixels from right edge
   - 🚨 CENTERING REQUIREMENT: Left and right margins should be EQUAL (±10px) to ensure horizontal centering
   - Bottom margin: minimum 40 pixels from bottom edge
   - Top of text area: At least 150 pixels from bottom edge for single-line titles
3. If text would extend beyond these margins, you MUST:
   - Wrap to 2-3 lines OR
   - Slightly reduce font size (but keep it LARGE and prominent)
4. 🚨 ABSOLUTE REQUIREMENT: NEVER allow any letter to touch or extend beyond the image edges
   - Each letter must be fully contained within the image boundaries
   - Check that first letter is at least 60px from left edge
   - Check that last letter is at least 60px from right edge
   - Check that bottom of letters is at least 40px from bottom edge
5. The title must be READABLE from a distance - use BOLD, LARGE text
6. CRITICAL FOR READABILITY: Add a semi-transparent dark gradient or solid bar behind the text
   - The gradient/bar should extend from the bottom edge upward to cover the text area
   - This ensures the title is readable even when overlaid on complex scene elements
   - Use 60-80% opacity black gradient fading upward, or a solid semi-transparent bar
7. Strong contrast: use bright, bold text colors against the dark background

NO OTHER TEXT ALLOWED:
- ONLY render the title "${title}"
- No credits, watermarks, camera info, dates, or any other text
- If tempted to add text besides "${title}", DON'T`;
}

/**
 * Check if we need high quality for final generation
 */
export function getImageQuality(isPreview: boolean, isCoverPage: boolean, languageCode: string): 'medium' | 'high' {
  // Check environment variable first
  const envQuality = process.env.IMAGE_QUALITY;
  
  // If environment variable is set to 'high', always use high quality
  // This prevents style drift between preview and final generation
  if (envQuality === 'high') {
    return 'high';
  }
  
  // For environments where IMAGE_QUALITY is not 'high':
  // Only use medium for non-cover preview pages to save costs
  if (isPreview && !isCoverPage) {
    return 'medium';
  }
  
  // Always use high quality for:
  // 1. Cover pages (preview or final)
  // 2. All final generation pages
  // This ensures style consistency
  return 'high';
}

/**
 * Log language decision for monitoring
 */
export function logLanguageDecision(
  bookId: string,
  languageCode: string,
  pageNumber: number,
  decision: 'embedded' | 'overlay'
): void {
  console.log('[Language Decision]', {
    bookId,
    languageCode,
    languageName: getLanguageName(languageCode),
    pageNumber,
    decision,
    timestamp: new Date().toISOString()
  });
}

// Export all language codes for reference
export const ALL_SUPPORTED_LANGUAGES = SUPPORTED_LANGUAGES.map(lang => lang.code);

export type SupportedLanguage = typeof ALL_SUPPORTED_LANGUAGES[number];
